<template lang="pug">
  form.product-form(@submit.prevent="addToCart", novalidate)
    .product-form__options
      option-select-buttons.product-form__options__option1(
        v-if="hasVariants"
        label="Color"
        :product="product"
        option="option1"
        v-model="selectedOption1"
      )
      option-select-buttons.product-form__options__option2(
        v-if="hasComplexVariants"
        label="Size"
        :product="product"
        option="option2"
        v-model="selectedOption2"
      )
    .product-form__atc
      base-button.product-form__submit(
        :disabled="isDisabled"
        :loading="adding"
      ) {{ buttonLabel }}
</template>

<script>
  /* eslint no-underscore-dangle: ["error", { "allow": ["_learnq"] }] */
  import { getVariantFromProduct } from 'scripts/helpers/product.js';
  import { purchaseTypeSingle, purchaseTypeRecurring } from 'scripts/config/subscriptions.js';

  import Icon from 'scripts/components/basic/Icon.vue';
  import RadioInput from 'scripts/components/forms/RadioInput.vue';
  import QuantityInput from 'scripts/components/forms/QuantityInput.vue';
  import OptionSelectDropdown from 'scripts/components/forms/OptionSelectDropdown.vue';
  import OptionSelectButtons from 'scripts/components/forms/OptionSelectButtons.vue';
  import SelectInput from 'scripts/components/forms/SelectInput.vue';
  import BaseButton from 'scripts/components/buttons/BaseButton.vue';
  import ProductPrice from 'scripts/components/product/ProductPrice.vue';

  export default {
    name: 'ProductForm',
    components: {
      Icon,
      RadioInput,
      QuantityInput,
      OptionSelectDropdown,
      OptionSelectButtons,
      SelectInput,
      BaseButton,
      ProductPrice,
    },
    props: {
      product: Object,
      subscriptionOptionBackgroundColor: String,
      subscriptionDisclaimerInfo: {
        type: Object,
        default: () => ({
          label: String,
          link: String,
        }),
      },
    },
    data() {
      return {
        purchaseTypeSingle,
        purchaseTypeRecurring,
        quantity: 1,
        adding: false,
        selectedPurchaseType: purchaseTypeSingle,
        selectedFrequency: '',
        selectedOption1: '',
        selectedOption2: '',
      };
    },
    computed: {
      isDisabled() {
        return this.adding || !this.currentVariantIsAvailable;
      },
      buttonLabel() {
        return this.currentVariantIsAvailable ? 'Add to Cart' : 'Sold Out';
      },
      // variant data
      hasVariants() {
        return this.product ? this.product.variants.length > 1 : false;
      },
      hasComplexVariants() {
        return this.product ? this.product.options.length > 1 : false;
      },
      currentVariant() {
        let currentVariant;

        if (this.hasComplexVariants) {
          currentVariant = this.product.variants.find((variant) => variant.option1 === this.selectedOption1 && variant.option2 === this.selectedOption2);
        } else if (this.hasVariants) {
          currentVariant = this.product.variants.find((variant) => variant.option1 === this.selectedOption1);
        }

        if (currentVariant) {
          return currentVariant;
        }

        // if we're still here... return the first variant
        return this.product.variants[0];
      },
      currentVariantIsAvailable() {
        if (this.currentVariant.inventory_management === '' || this.currentVariant.inventory_policy === 'continue') {
          return true;
        }

        return this.remainingQuantity > 0;
      },
      remainingQuantity() {
        return this.currentVariant.remaining_quantity;
      },
      currentPrice() {
        return this.currentVariant.price;
      },
      hasSubscription() {
        return Boolean(this.currentVariant.subscription_variant_id);
      },
      isSubscriptionSelected() {
        return this.selectedPurchaseType === purchaseTypeRecurring;
      },
      subscriptionDiscount() {
        if (!this.hasSubscription) return 0;

        return parseInt(this.product.subscription.discount_percentage, 0);
      },
      subscriptionUnitType() {
        return this.hasSubscription ? this.product.subscription.shipping_interval_unit_type : '';
      },
      subscriptionFrequencies() {
        const frequencies = [];

        if (!this.hasSubscription) {
          return frequencies;
        }

        this.product.subscription.shipping_interval_frequency.split(',').forEach((frequency) => {
          frequencies.push({
            value: frequency,
            label: `${frequency} ${this.subscriptionUnitType}`,
          });
        });

        return frequencies;
      },
    },
    methods: {
      publishToKlaviyo(product) {
        const item = {
          Name: product.title,
          ProductID: product.id,
          Categories: product.collections,
          ImageURL: `https:${product.images[0]}`,
          URL: product.url,
          Brand: product.vendor,
          Price: product.price,
          CompareAtPrice: product.compare_at_price,
        };

        // klaviyo
        if (window._learnq) {
          window._learnq.push(['track', 'Added to Cart', item]);
        }
      },
      handleSubscriptionOptionChange({ purchaseType, frequency }) {
        this.selectedPurchaseType = purchaseType;
        this.selectedFrequency = frequency;
      },
      addToCart() {
        const addProduct = {
          id: this.currentVariant.id,
          quantity: this.quantity,
        };

        this.adding = true;

        this.publishToKlaviyo(this.product);

        this.$store
          .dispatch('cart/addItem', addProduct)
          .then(() => this.$store.dispatch('cart/openSidecart'))
          .catch((error) => this.$store.dispatch('toast/send', { text: error, type: 'error' }))
          .then(() => {
            this.adding = false;
          });
      },
      setDefaultOption() {
        const defaultVariant = this.variantParam ? getVariantFromProduct(this.product, this.variantParam) : this.product.variants[0];

        if (this.$refs.variantSelector) {
          this.$refs.variantSelector.update(defaultVariant.option1);
        }
      },
    },
  };
</script>

<style lang="scss">
  .product-form {
    margin: 0 0 ($spacing-unit * 8);
  }

  .product-form__atc {
    display: flex;
    height: 50px;
    max-width: 425px;
  }

  .product-form__submit {
    margin: 0;
    width: 100%;
    color: $color-white;
    font-size: $font-size-body-large;
    line-height: 20px;
    background: var(--pdp-color);
    border-color: var(--pdp-color);

    &:hover {
      color: var(--pdp-color);
    }

    @include tablet-up {
      max-width: 250px;
    }
  }

  .product-form__quantity {
    margin: 0;
    width: 91px;
    background: $color-green-900;

    @include tablet-up {
      width: 112px;
    }
  }
</style>
